import { Injectable } from '@angular/core';
import { SupabaseService } from 'app/core/supabase/supabase.service';
import {
    DbNotification,
    mapDbToNotification,
    Notification,
    NotificationCategory,
} from 'app/layout/common/notifications/notifications.types';
import { from, map, Observable, ReplaySubject, tap } from 'rxjs';
import { RealtimeChannel } from '@supabase/supabase-js';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);
    private _currentNotifications: Notification[] = [];
    private _realtimeChannel: RealtimeChannel | null = null;

    constructor(private _supabaseService: SupabaseService) {
        // Cleanup realtime on sign-out
        this._supabaseService.onAuthStateChange((event) => {
            if (event === 'SIGNED_OUT') {
                this._realtimeChannel?.unsubscribe();
                this._realtimeChannel = null;
                this._currentNotifications = [];
                this._notifications.next([]);
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    /** Only chat/message notifications (shown under the conversations cloud icon). */
    get conversationNotifications$(): Observable<Notification[]> {
        return this.notifications$.pipe(
            map((list) => list.filter((n) => n.category === 'conversation'))
        );
    }

    /** Only general business notifications (shown under the bell icon). */
    get generalNotifications$(): Observable<Notification[]> {
        return this.notifications$.pipe(
            map((list) => list.filter((n) => n.category !== 'conversation'))
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getAll(): Observable<Notification[]> {
        return from(this._fetchNotifications()).pipe(
            tap((notifications) => {
                this._currentNotifications = notifications;
                this._notifications.next(notifications);
                this._subscribeToRealtime();
            })
        );
    }

    update(id: string, notification: Notification): Observable<Notification> {
        const readAt = notification.read
            ? new Date().toISOString()
            : null;

        return from(
            this._supabaseService.client
                .from('notifications')
                .update({
                    read_at: readAt,
                    status: notification.read ? 'read' : 'delivered',
                })
                .eq('id', id)
                .select('*')
                .single()
        ).pipe(
            map(({ data }) => {
                const mapped = mapDbToNotification(data as DbNotification);
                const index = this._currentNotifications.findIndex(
                    (n) => n.id === id
                );
                if (index !== -1) {
                    this._currentNotifications[index] = mapped;
                    this._notifications.next([
                        ...this._currentNotifications,
                    ]);
                }
                return mapped;
            })
        );
    }

    delete(id: string): Observable<boolean> {
        return from(
            this._supabaseService.client
                .from('notifications')
                .delete()
                .eq('id', id)
        ).pipe(
            map(({ error }) => {
                if (!error) {
                    this._currentNotifications =
                        this._currentNotifications.filter(
                            (n) => n.id !== id
                        );
                    this._notifications.next([
                        ...this._currentNotifications,
                    ]);
                }
                return !error;
            })
        );
    }

    /**
     * Mark notifications as read. When a category is provided, only that
     * inbox (conversations / general) is affected; otherwise all are marked.
     */
    markAllAsRead(category?: NotificationCategory): Observable<boolean> {
        return from(this._markAllRead(category)).pipe(
            tap(() => {
                this._currentNotifications =
                    this._currentNotifications.map((n) =>
                        !category || n.category === category
                            ? { ...n, read: true }
                            : n
                    );
                this._notifications.next([
                    ...this._currentNotifications,
                ]);
            }),
            map(() => true)
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private async _fetchNotifications(): Promise<Notification[]> {
        const user = await this._supabaseService.getCurrentUser();
        if (!user) return [];

        const { data, error } = await this._supabaseService.client
            .from('notifications')
            .select('*')
            .eq('recipient_user_id', user.id)
            .eq('channel', 'in_app')
            .order('created_at', { ascending: false })
            .limit(50);

        if (error || !data) return [];
        return (data as DbNotification[]).map(mapDbToNotification);
    }

    private async _markAllRead(category?: NotificationCategory): Promise<void> {
        const user = await this._supabaseService.getCurrentUser();
        if (!user) return;

        // When scoping to a category, target only the locally-known unread
        // ids of that category (category is derived from template_code, so we
        // avoid duplicating that classification logic in SQL).
        if (category) {
            const ids = this._currentNotifications
                .filter((n) => n.category === category && !n.read)
                .map((n) => n.id);

            if (ids.length === 0) return;

            await this._supabaseService.client
                .from('notifications')
                .update({
                    read_at: new Date().toISOString(),
                    status: 'read',
                })
                .in('id', ids);
            return;
        }

        await this._supabaseService.client
            .from('notifications')
            .update({
                read_at: new Date().toISOString(),
                status: 'read',
            })
            .eq('recipient_user_id', user.id)
            .eq('channel', 'in_app')
            .is('read_at', null);
    }

    private async _subscribeToRealtime(): Promise<void> {
        if (this._realtimeChannel) {
            this._realtimeChannel.unsubscribe();
        }

        const user = await this._supabaseService.getCurrentUser();
        if (!user) return;

        this._realtimeChannel = this._supabaseService.client
            .channel('notifications-' + user.id)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `recipient_user_id=eq.${user.id}`,
                },
                (payload) => {
                    const db = payload.new as DbNotification;
                    if (db.channel !== 'in_app') return;
                    const notification = mapDbToNotification(db);
                    this._currentNotifications = [
                        notification,
                        ...this._currentNotifications,
                    ];
                    this._notifications.next([
                        ...this._currentNotifications,
                    ]);
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `recipient_user_id=eq.${user.id}`,
                },
                (payload) => {
                    const db = payload.new as DbNotification;
                    const notification = mapDbToNotification(db);
                    const index = this._currentNotifications.findIndex(
                        (n) => n.id === notification.id
                    );
                    if (index !== -1) {
                        this._currentNotifications[index] = notification;
                        this._notifications.next([
                            ...this._currentNotifications,
                        ]);
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `recipient_user_id=eq.${user.id}`,
                },
                (payload) => {
                    const id = (payload.old as any).id;
                    this._currentNotifications =
                        this._currentNotifications.filter(
                            (n) => n.id !== id
                        );
                    this._notifications.next([
                        ...this._currentNotifications,
                    ]);
                }
            )
            .subscribe();
    }
}
